import type { RaterRelationshipOption } from "~vue/api/generated/reflections";

export type DateStyle = "default" | "short" | "long";

export function formatDate(iso: string | null | undefined, style: DateStyle = "default"): string {
    if (!iso) {
        return "";
    }
    try {
        const d = new Date(iso);
        if (style === "short") {
            return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        }
        if (style === "long") {
            return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
        }
        return d.toLocaleDateString();
    } catch {
        return "";
    }
}

const DAY_MS = 1000 * 60 * 60 * 24;
const RELATIVE_LABEL_DAYS = 7;

function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// Null when the cycle has no deadline — a DRAFT cycle outside a campaign has no
// `closes_at` until launch — so callers omit the label rather than render
// "Closes" with a blank date.
export function closesLabel(iso: string | null | undefined): string | null {
    if (!iso) {
        return null;
    }
    const closesAt = Date.parse(iso);
    if (!Number.isFinite(closesAt)) {
        return null;
    }
    const now = Date.now();
    if (closesAt <= now) {
        return "Closed";
    }
    const days = Math.round((startOfDay(new Date(closesAt)) - startOfDay(new Date(now))) / DAY_MS);
    if (days === 0) {
        return "Closes today";
    }
    if (days === 1) {
        return "Closes tomorrow";
    }
    if (days <= RELATIVE_LABEL_DAYS) {
        return `Closes in ${days} days`;
    }
    const short = formatDate(iso, "short");
    return short ? `Closes ${short}` : null;
}

export function inviteStateChipState(state: string) {
    switch (state) {
        case "responded":
            return "done";
        case "pending":
            return "pending";
        case "revoked":
        case "expired":
            return "neutral";
        default:
            return "neutral";
    }
}

export function inviteStateLabel(state: string): string {
    switch (state) {
        case "responded":
            return "Submitted";
        case "pending":
            return "Pending";
        case "revoked":
            return "Revoked";
        case "expired":
            return "Expired";
        default:
            return state;
    }
}

export function cycleStateLabel(state: string | undefined): string {
    switch (state) {
        case "draft":
            return "Draft";
        case "collecting":
            return "In progress";
        case "unlocked":
            return "Results ready";
        case "closed":
            return "Closed";
        default:
            return state ?? "";
    }
}

export function cycleStateChipState(state: string | undefined) {
    switch (state) {
        case "unlocked":
            return "done";
        case "collecting":
            return "active";
        case "closed":
            return "neutral";
        default:
            return "pending";
    }
}

// The categories every organization had before they became configurable. A payload
// from a coach_api that predates the field carries no list, and a picker with no
// options strands the rater — fall back to these rather than rendering nothing.
export const DEFAULT_RATER_RELATIONSHIPS: RaterRelationshipOption[] = [
    { value: "peer", label: "Peer" },
    { value: "manager", label: "Manager" },
    { value: "direct", label: "Direct report" },
    { value: "other", label: "Other" },
];

// coach_api rejects a relationship the organization has since deactivated. The
// admin can change the list while a nomination or rater page is open, so the
// caller re-reads the current categories instead of retrying the stale one.
export function isRelationshipRejected(errorBody: unknown): boolean {
    return typeof errorBody === "object" && errorBody !== null && (errorBody as { code?: string }).code === "invalid_relationship";
}

const RELATIONSHIP_OPTION_BODIES: Record<RaterRelationshipOption["value"], (name: string) => string> = {
    peer: () => "Same level — we work together but I don't manage them.",
    manager: (name) => `I manage ${name}.`,
    skip_manager: (name) => `I manage ${name}'s manager.`,
    direct: (name) => `${name} is my manager.`,
    other: () => "Cross-functional partner, mentor, etc.",
    self: () => "",
};

// Helper copy under each option on the rater relationship picker.
export function relationshipOptionBody(value: RaterRelationshipOption["value"], subjectFirstName: string): string {
    return RELATIONSHIP_OPTION_BODIES[value](subjectFirstName);
}

const SELF_VS_OTHERS_GAP_THRESHOLD = 0.7;

export function isBigGap(self: number | null, others: number | null): boolean {
    if (self === null || others === null) {
        return false;
    }
    return Math.abs(self - others) >= SELF_VS_OTHERS_GAP_THRESHOLD;
}

export function formatGap(diff: number): string {
    const sign = diff > 0 ? "+" : "";
    const big = Math.abs(diff) >= SELF_VS_OTHERS_GAP_THRESHOLD;
    const tag = big ? (diff > 0 ? " · you rate higher" : " · others rate higher") : "";
    return `${sign}${diff.toFixed(1)}${tag}`;
}

// Whether the subject may nudge or remove this rater. Normally only a rater who
// hasn't answered yet, but an organization that hides rater status sends no
// `state`, and gating on it would say who has already responded — so every invite
// the subject can see is actionable there, and the API decides the real outcome.
export function canActOnInvite(cycle: { rater_status_hidden?: boolean } | null | undefined, invite: { state?: string | null }): boolean {
    return cycle?.rater_status_hidden === true || invite.state === "pending";
}
